import { useEffect } from 'react';
import { create } from 'zustand';
import { useAuthStore } from '@/stores/authStore';
import { GameStateTransport, type GameStateConnectionConfig, type GameStateConnectionStatus,
  type GameStateSocket } from '@/lib/gameState/transport';
import { parseGameIdentity, type AdmittedGameState, type GameStateComparison } from '@/lib/gameState/admission';

/** The override is a base URL, not the legacy analytics base. An absent
 * override leaves legacy scores/consensus alone and opens no Core socket.
 * Never borrow the analytics bearer for an explicitly configured Core.
 */
export function gameStateConfig(env: Record<string, unknown>): GameStateConnectionConfig | null {
  const baseUrl = typeof env.VITE_HYDRA_GAMESTATE_URL === 'string' ? env.VITE_HYDRA_GAMESTATE_URL.trim() : '';
  if (!baseUrl) return null;
  const token = [env.VITE_HYDRA_GAMESTATE_TOKEN, env.VITE_HYDRA_CORE_TOKEN]
    .find(value => typeof value === 'string' && value.trim()) as string | undefined;
  return { baseUrl, token: token?.trim() ?? '' };
}
const config = gameStateConfig(import.meta.env);

interface CoreGameStatesView {
  configured: boolean;
  status: GameStateConnectionStatus;
  error: string | null;
  records: ReadonlyMap<string, AdmittedGameState>;
  comparisons: ReadonlyMap<string, GameStateComparison>;
}
export const useCoreGameStatesStore = create<CoreGameStatesView>(() => ({
  configured: config !== null, status: 'stopped', error: null, records: new Map(), comparisons: new Map(),
}));

const interests = new Map<symbol, string>();
let owner: GameStateTransport | null = null;
let authScope: string | null = null;
let removeVisibility: (() => void) | null = null;
let removeAuth: (() => void) | null = null;

function clearOwner(): void {
  owner?.stop();
  owner = null;
  removeVisibility?.(); removeVisibility = null;
  removeAuth?.(); removeAuth = null;
  authScope = null;
  useCoreGameStatesStore.setState({ status: 'stopped', error: null, records: new Map(), comparisons: new Map() });
}

function socketFor(url: string): GameStateSocket {
  const ws = new WebSocket(url);
  const socket: GameStateSocket = { onopen: null, onmessage: null, onerror: null, onclose: null,
    send: data => ws.send(data), close: () => ws.close() };
  ws.onopen = () => socket.onopen?.();
  ws.onmessage = event => socket.onmessage?.({ data: event.data });
  ws.onerror = () => socket.onerror?.();
  ws.onclose = () => socket.onclose?.();
  return socket;
}

function currentAuthScope(): string | null {
  const auth = useAuthStore.getState();
  return auth.status === 'authenticated' && auth.user
    ? JSON.stringify([auth.user.id, auth.user.orgId, auth.user.role]) : null;
}

function reconcile(): void {
  const nextScope = currentAuthScope();
  if (!config || !interests.size || !nextScope) { clearOwner(); return; }
  if (owner && authScope === nextScope) { owner.setKeys([...interests.values()]); return; }
  clearOwner();
  authScope = nextScope;
  try {
    owner = new GameStateTransport({ config, keys: [...interests.values()], socket: socketFor,
      read: async (url, init) => {
        const response = await fetch(url, init);
        if (!response.ok) throw new Error('GameState comparison unavailable');
        return response.json();
      },
      visible: () => !document.hidden,
      changed: transport => useCoreGameStatesStore.setState({ status: transport.status, error: transport.error,
        records: transport.admission.snapshot(), comparisons: transport.admission.comparisons() }),
    });
    const onVisibility = () => { if (!document.hidden) owner?.refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    removeAuth = useAuthStore.subscribe(() => {
      if (currentAuthScope() !== authScope) reconcile();
    });
    owner.start();
  } catch {
    clearOwner();
    useCoreGameStatesStore.setState({ status: 'disconnected', error: 'GameState configuration unavailable' });
  }
}

/** One gamestate-only socket and one unioned batch-poll owner per application,
 * independent of the market feed's direct/SharedWorker mode. No per-game
 * transport. Removing the final reader tears down all timers and listeners.
 */
export function useCoreGameStateInterest(key: string | null): void {
  const auth = useAuthStore(s => s.status === 'authenticated' ? s.user : null);
  const scope = auth ? JSON.stringify([auth.id, auth.orgId, auth.role]) : null;
  useEffect(() => {
    if (!config || !scope || !key || !parseGameIdentity(key)) return;
    const id = Symbol();
    interests.set(id, key);
    reconcile();
    return () => { interests.delete(id); reconcile(); };
  }, [key, scope]);
}
